import { put, call, takeLatest, select } from "redux-saga/effects";
import {
  requestMobileStoreFeed,
  fetchFeedFailure,
  setSelectedStoreFeed,
  requestDesktopFeed,
  setDesktopFeed,
} from "../../slices/dynamicHomeSlice";
import { getPageFeedAPI } from "../../../api/homeApi";

// ---------------------------------------------------------------------------
// Worker saga
// ---------------------------------------------------------------------------

/**
 * Fetches the feed for the tapped brand-tab store and writes it into Redux.
 * Skips the API call entirely if the feed is already cached.
 * @param {{ payload: { storeId: string, storeName?: string, stores: Array, pincode: string, email: string, fnpId: string, forceCall?: boolean } }} action
 */
function* fetchMobileFeedSaga(action) {
  const { storeId, storeName, pincode, email, fnpId, forceCall } = action.payload;

  // Guard: skip API call if feed already cached — unless forceCall overrides it
  const pageStores = yield select((state) => state.dynamicHome.pageStores);
  const cachedStore = pageStores.find((s) => s.storeId === storeId);
  if (cachedStore?.feed && !forceCall) {
    return;
  }

  const mastheadStoreName = storeName || cachedStore?.storeName || cachedStore?.name;

  try {
    const response = yield call(getPageFeedAPI, {
      isMobile: true,
      storeName: mastheadStoreName,
      pincode,
      email,
      fnpId,
    });
    yield put(setSelectedStoreFeed({ storeId, response }));
  } catch (error) {
    yield put(fetchFeedFailure(error?.message || "Failed to fetch brand feed"));
  }
}

// ---------------------------------------------------------------------------
// Watcher saga
// ---------------------------------------------------------------------------

/**
 * Fetches a fresh desktop home feed after a location change and updates desktopWidgets.
 * @param {{ payload: { pincode: string, email: string, fnpId: string } }} action
 */
function* fetchDesktopFeedSaga(action) {
  const { pincode, email, fnpId } = action.payload;
  try {
    const response = yield call(getPageFeedAPI, { isMobile: false, pincode, email, fnpId });
    yield put(setDesktopFeed(response?.widgets ?? []));
  } catch (error) {
    yield put(fetchFeedFailure(error?.message || "Failed to fetch desktop feed"));
  }
}

/**
 * Watches for `requestMobileStoreFeed` actions dispatched from BrandTabs and LocationLock.
 * Watches for `requestDesktopFeed` actions dispatched from LocationLock on the home page.
 * Uses `takeLatest` so rapid dispatches cancel in-flight requests.
 */
export function* watchDynamicHomeSaga() {
  yield takeLatest(requestMobileStoreFeed.type, fetchMobileFeedSaga);
  yield takeLatest(requestDesktopFeed.type, fetchDesktopFeedSaga);
}
